import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@components/atoms/Button';

const SUPPORT_EMAIL = 'support@getzenhammer.com';

interface FreeTrialLimitNoticeProps {
  used: number;
  limit: number;
  /** Switch to the paid flow — this both creates the organization and frees nothing, but
   *  it is the immediate way forward. */
  onContinueWithCard?: () => void;
  onCancel?: () => void;
}

/**
 * Shown when a user tries to create a fourth non-paying organization.
 *
 * Explains rather than just refuses, and offers the two real ways forward. Worth knowing
 * why "add a card" is the primary action: the cap counts expired trials as well as live
 * ones, so paying for any existing organization moves it to ACTIVE and frees a slot. The
 * cap therefore resolves through payment — there is no "reset my trials" path to offer,
 * and none is needed.
 */
export const FreeTrialLimitNotice: React.FC<FreeTrialLimitNoticeProps> = ({
  used,
  limit,
  onContinueWithCard,
  onCancel,
}) => {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-3">
        <AlertCircle
          className="h-6 w-6 flex-shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            You've reached the free trial limit
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            You already have {used} of {limit} organizations on a free trial.
            Organizations whose trial has ended still count, because their data
            is kept for you.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/60">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          How to create another
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-400">
          <li>
            Add a payment method to one of your existing organizations — that
            frees a slot straight away
          </li>
          <li>Or create this one with a card now, which does not use a slot</li>
        </ul>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        Need something different? Email us at{' '}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-indigo-600 hover:underline dark:text-indigo-400">
          {SUPPORT_EMAIL}
        </a>{' '}
        and we'll sort it out.
      </p>

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {onContinueWithCard && (
          <Button type="button" onClick={onContinueWithCard}>
            Continue with a card
          </Button>
        )}
      </div>
    </div>
  );
};

export default FreeTrialLimitNotice;
